import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const reviewSlice = createSlice({
    name: "review",
    initialState: {
        page: 0,
        pageCount: 0,
        totalRow: 0,
        listData: null,
        statsData: null,
        detailData: null,
        exporting: false,
        errorDetailData: null,
    },
    reducers: {
        doGetReviewListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.totalRow = action.payload.total_row;
        },
        doGetReviewListFail: (state) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
        },
        doGetReviewStatsSuccess: (state, action) => {
            state.statsData = action.payload;
        },
        doGetReviewStatsFail: (state) => {
            state.statsData = null;
        },
        doGetReviewDetailSuccess: (state, action) => {
            state.detailData = action.payload.review;
        },
        doGetReviewDetailFail: (state, action) => {
            state.detailData = null;
            state.errorDetailData = action.payload;
        },
        setReviewExporting: (state, action) => {
            state.exporting = action.payload;
        },
        resetReviewState: (state) => {
            state.errorDetailData = null;
        },
        resetReviewDetailState: (state) => {
            state.detailData = null;
            state.errorDetailData = null;
        },
    },
});

const buildReviewListUrl = (param) => {
    let url = `/api/admin/reviews?page=${param.page}&size=${param.limit}`;
    if (param.review_id) url += `&review_id=${encodeURIComponent(param.review_id)}`;
    if (param.deal_id) url += `&deal_id=${encodeURIComponent(param.deal_id)}`;
    if (param.date_from) url += `&date_from=${param.date_from}`;
    if (param.date_to) url += `&date_to=${param.date_to}`;
    if (param.sort_by) url += `&sort_by=${param.sort_by}`;
    return url;
};

export const doGetReviewList = (param) => async (dispatch) => {
    try {
        const response = await axios.get(buildReviewListUrl(param));
        dispatch(doGetReviewListSuccess(response.data));
    } catch (error) {
        dispatch(doGetReviewListFail(error.response));
    }
};

export const doGetReviewStats = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/admin/reviews/stats");
        dispatch(doGetReviewStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetReviewStatsFail(error.response));
    }
};

// The detail is keyed by deal: the review pair exists from the moment the
// tenancy completes, before either side has written anything.
export const doGetReviewDetail = (dealId) => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/reviews/${dealId}`);
        dispatch(doGetReviewDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetReviewDetailFail(error.response));
    }
};

// ---- CSV export --------------------------------------------------------
// There is no server-side export endpoint, so the whole filtered set is pulled
// through the list endpoint (which caps `size` at 100) and the CSV is built in
// the browser — same approach as the payout control export.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 50;

const csvDate = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d)) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const STATUS_LABEL = { REVIEWED: "Reviewed", PENDING: "Pending", EXPIRED: "Expired" };
const statusLabel = (s) => STATUS_LABEL[s] || s || "";

export const doExportReviews = (param) => async (dispatch) => {
    try {
        dispatch(setReviewExporting(true));

        const rows = [];
        for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
            const url = buildReviewListUrl({ ...param, page, limit: EXPORT_PAGE_SIZE });
            const response = await axios.get(url);
            const batch = response.data?.data || [];
            rows.push(...batch);
            if (batch.length < EXPORT_PAGE_SIZE) break;
        }

        const header = [
            "Review ID", "Host", "Tenant", "Deal ID", "Deal Completed Date",
            "Host Review", "Tenant Review",
        ];
        const body = rows.map((r) => [
            r.review_id, r.host_name, r.tenant_name, r.deal_code, csvDate(r.completed_at),
            statusLabel(r.host_review_status), statusLabel(r.tenant_review_status),
        ]);
        const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");

        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const now = new Date();
        link.setAttribute("download", `reviews_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setReviewExporting(false));
    }
};

export const {
    doGetReviewListSuccess, doGetReviewListFail,
    doGetReviewStatsSuccess, doGetReviewStatsFail,
    doGetReviewDetailSuccess, doGetReviewDetailFail,
    setReviewExporting,
    resetReviewState, resetReviewDetailState,
} = reviewSlice.actions;

export const getReviewList = (state) => state.review?.listData;
export const getReviewListPage = (state) => state.review?.page;
export const getReviewListPageCount = (state) => state.review?.pageCount;
export const getReviewListTotalRow = (state) => state.review?.totalRow;
export const getReviewStats = (state) => state.review?.statsData;
export const getReviewDetail = (state) => state.review?.detailData;
export const getReviewExporting = (state) => state.review?.exporting;
export const getReviewErrorDetailData = (state) => state.review?.errorDetailData;

export default reviewSlice.reducer;
